<template>
    <version-modal-form v-if="showVersionForm" :api-route="'quiz'" :model="type" @version="version" @close="close">
        <template #preview>
            <p>
                To view the current version of the {{ type }} click
                <e-tag class="clickable is-clickable" type="is-link is-light" @click="preview()">Preview</e-tag>
            </p>
        </template>
    </version-modal-form>
    <div v-if="showProcessingModal" class="modal-card" style="width: auto">
        <header class="modal-card-head">
            <p class="modal-card-title">Creating New Version</p>
        </header>
        <section class="modal-card-body">
            <div v-if="isProcessing && !error" class="version-processing-state generation-status">
                <div class="tw:mb-4">
                    <e-icon icon="spinner" size="is-large" style="color: var(--p-primary-color)" />
                </div>
                <p class="tw:text-base tw:font-semibold has-text-grey-dark tw:mb-2">Creating new version...</p>
                <p class="tw:text-sm has-text-grey tw:max-w-md tw:leading-relaxed tw:text-center tw:mb-4">
                    Please wait while your {{ type }} is being versioned.
                </p>
                <div class="version-processing-progress">
                    <e-progress indeterminate size="is-small" type="is-primary" />
                </div>
            </div>
            <p v-else-if="error" class="version-processing-error has-text-danger">{{ error }}</p>
        </section>
        <footer class="modal-card-foot tw:flex tw:gap-2">
            <e-button v-if="error" type="is-primary" @click="retryVersion">Retry</e-button>
            <e-button v-if="error" @click="close">Cancel</e-button>
        </footer>
    </div>
</template>

<script>
import VersionModalForm from '@/components/version/VersionModalForm.vue';

import axios from '@/plugins/axios.js';

export default {
    name: 'QuizVersionModalForm',
    components: { VersionModalForm },

    props: {
        quiz: {
            type: Object,
            required: true,
        },
    },

    emits: ['close', 'preview'],

    data() {
        return {
            isProcessing: false,
            error: null,
            channel: null,
            pollTimeout: null,
            pollDelayMs: 2000,
            pollTimer: null,
        };
    },

    computed: {
        type() {
            return this.quiz.type ? this.quiz.type : 'quiz';
        },

        showVersionForm() {
            return !this.isProcessing && !this.error;
        },

        showProcessingModal() {
            return this.isProcessing || this.error;
        },
    },

    async mounted() {
        await this.initializeEchoListener();
    },

    beforeUnmount() {
        this.clearPolling();
        this.removeEchoListener();
    },

    methods: {
        async initializeEchoListener() {
            this.channel = this.$echo
                .private(`quiz.version.${this.quiz.id}`)
                .listen('QuizVersionCompleted', event => this.handleVersionComplete(event));
        },

        removeEchoListener() {
            if (this.channel) {
                this.channel.stopListening('QuizVersionCompleted');
            }
        },

        async version() {
            this.isProcessing = true;
            this.error = null;

            try {
                const initialVersionCount = await this.getVersionCount();

                await axios.post(`/api/version-quiz/${this.quiz.id}`);
                this.startPollingForCompletion(initialVersionCount);
            } catch (error) {
                this.clearPolling();
                this.isProcessing = false;
                this.error = 'Failed to start version creation. Please try again.';
                console.error('Quiz versioning error:', error);
            }
        },

        versionResourceType() {
            return this.quiz.type ? 'surveys' : 'quizzes';
        },

        versionResourcePath(versionableId) {
            return this.quiz.type ? `/surveys/${versionableId}` : `/quizzes/${versionableId}`;
        },

        async getVersionCount() {
            const { data } = await axios.get(`/api/${this.versionResourceType()}/${this.quiz.id}/get-all-versions`);

            return (data.versions || []).length;
        },

        startPollingForCompletion(initialVersionCount) {
            this.clearPolling();
            this.pollDelayMs = 2000;

            const poll = async () => {
                try {
                    const { data } = await axios.get(
                        `/api/${this.versionResourceType()}/${this.quiz.id}/get-all-versions`
                    );
                    const versions = data.versions || [];

                    if (versions.length > initialVersionCount) {
                        const latestVersion =
                            versions.find(version => version.is_latest) || versions[versions.length - 1];

                        if (latestVersion?.versionable_id) {
                            this.completeVersionRedirect(latestVersion.versionable_id);

                            return;
                        }
                    }
                } catch (error) {
                    console.error('Quiz version polling error:', error);
                }

                this.pollDelayMs = Math.min(this.pollDelayMs * 2, 30000);
                this.pollTimer = window.setTimeout(poll, this.pollDelayMs);
            };

            this.pollTimer = window.setTimeout(poll, this.pollDelayMs);

            this.pollTimeout = window.setTimeout(() => {
                this.clearPolling();
                this.isProcessing = false;
                this.error = 'Version creation is taking longer than expected. Please try again.';
            }, 300000);
        },

        clearPolling() {
            if (this.pollTimer) {
                window.clearTimeout(this.pollTimer);
                this.pollTimer = null;
            }

            if (this.pollTimeout) {
                window.clearTimeout(this.pollTimeout);
                this.pollTimeout = null;
            }
        },

        completeVersionRedirect(versionableId) {
            this.clearPolling();
            window.location.replace(this.versionResourcePath(versionableId));
        },

        handleVersionComplete(event) {
            this.completeVersionRedirect(event.new_version_id);
        },

        async retryVersion() {
            this.error = null;
            await this.version();
        },

        async close() {
            if (this.$route.name === 'quiz-view') {
                this.$router.push({ name: 'quizzes-index' });
            } else if (this.$route.name === 'survey-view') {
                this.$router.push({ name: 'surveys-index' });
            } else {
                window.location.reload();
            }

            this.$emit('close');
        },

        async preview() {
            this.$emit('close');
            this.$emit('preview');
        },
    },
};
</script>

<style scoped>
.modal-card {
    margin: 0 auto;
    max-width: 500px;
}

.generation-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem 1rem;
    min-height: 6em;
}

.version-processing-progress {
    width: 100%;
    max-width: 20rem;
}

.version-processing-error {
    margin: 0;
    padding: 1rem;
    text-align: center;
}
</style>
